import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { FiTrendingUp, FiAlertCircle, FiZap, FiImage, FiFileText, FiArrowUp, FiUsers, FiCheck, FiStar, FiCheckCircle, FiRepeat, FiX } from 'react-icons/fi';
import PRICING_CONFIG from '../config/pricing';

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// Convert config subscriptions to API format for fallback
const configSubscriptionsToApiFormat = () => {
  return PRICING_CONFIG.SUBSCRIPTIONS.map(s => ({
    id: s.id,
    name: s.name,
    price: s.price,
    price_display: s.priceDisplay,
    credits: s.credits,
    ideas_equivalent: s.ideasEquivalent,
    description: s.description,
    is_popular: s.isPopular,
    interval: 'monthly'
  }));
};

// Convert config packs to API format for fallback
const configPacksToApiFormat = () => {
  return PRICING_CONFIG.CREDIT_PACKS.map(p => ({
    id: p.id,
    name: p.name,
    price: p.price,
    price_display: p.priceDisplay,
    credits: p.credits,
    ideas_equivalent: p.ideasEquivalent,
    description: p.description,
    is_popular: p.isPopular || false
  }));
};

function UsageDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [usage, setUsage] = useState(null);
  const [moderation, setModeration] = useState(null);
  const [plans, setPlans] = useState([]);
  const [creditPacks, setCreditPacks] = useState(configPacksToApiFormat()); // Use config as default
  const [subscriptionPlans, setSubscriptionPlans] = useState(configSubscriptionsToApiFormat()); // Use config as default
  const [userSubscription, setUserSubscription] = useState(null);
  const [overageRate, setOverageRate] = useState(25);
  const [loading, setLoading] = useState(true);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [buyingPack, setBuyingPack] = useState(null);
  const [subscribingPlan, setSubscribingPlan] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [purchaseMode, setPurchaseMode] = useState('subscription'); // 'subscription' or 'one-time'
  const [paymentMessage, setPaymentMessage] = useState(null);


  useEffect(() => {
    loadData();
    loadRazorpayScript(); // Pre-load Razorpay script
    
    // Check for payment status in URL
    const payment = searchParams.get('payment');
    const orderId = searchParams.get('order_id');
    
    if (payment === 'success' && orderId) {
      checkPaymentStatus(orderId);
    } else if (payment === 'cancelled') {
      setPaymentMessage({ type: 'info', text: 'Payment was cancelled. No changes made to your plan.' });
      // Clear URL params
      setSearchParams({});
    }
  }, []);

  const checkPaymentStatus = async (orderId) => {
    try {
      const result = await api.getPaymentStatus(orderId);
      if (result.payment_status === 'paid') {
        setPaymentMessage({ 
          type: 'success', 
          text: result.message || `Successfully upgraded to ${result.plan_name} plan!` 
        });
        // Reload data to get updated subscription
        await loadData();
      } else {
        setPaymentMessage({ 
          type: 'info', 
          text: 'Payment is being processed. Please refresh in a moment.' 
        });
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    } finally {
      // Clear URL params
      setSearchParams({});
    }
  };

  const loadData = async () => {
    try {
      // Load usage, plans, credit packs, and subscription plans
      const [usageData, plansData, packsData, subsData, userSubData] = await Promise.all([
        api.getUserUsage(),
        api.getPlans(),
        api.getCreditPacks().catch(() => ({ packs: configPacksToApiFormat() })),
        api.getSubscriptionPlans().catch(() => ({ subscriptions: configSubscriptionsToApiFormat() })),
        api.getUserSubscription().catch(() => ({ has_subscription: false }))
      ]);
      setUsage(usageData);
      setPlans(plansData.plans || []);
      setOverageRate(plansData.overage_rate || 25);
      // Use API data if available, otherwise keep config defaults
      if (packsData.packs && packsData.packs.length > 0) {
        setCreditPacks(packsData.packs);
      }
      if (subsData.subscriptions && subsData.subscriptions.length > 0) {
        setSubscriptionPlans(subsData.subscriptions);
      }
      setUserSubscription(userSubData.has_subscription ? userSubData.subscription : null);
      
      // Moderation stats is optional - don't fail if it errors
      try {
        const moderationData = await api.getModerationStats();
        setModeration(moderationData);
      } catch (modError) {
        console.log('Moderation stats not available');
        setModeration({ violations: 0, warnings: 0 });
      }
    } catch (error) {
      console.error('Error loading usage data:', error);
      // Still allow page to render with defaults from config
      setUsage({
        tier: 'free',
        credits: { 
          used: 0, 
          limit: PRICING_CONFIG.FREE_CREDITS, 
          remaining: PRICING_CONFIG.FREE_CREDITS, 
          ideas_equivalent: PRICING_CONFIG.FREE_POSTS_EQUIVALENT, 
          bonus_credits: 0, 
          total_available: PRICING_CONFIG.FREE_CREDITS 
        },
        limits: { 
          ideas_equivalent: PRICING_CONFIG.FREE_POSTS_EQUIVALENT, 
          credits_per_month: PRICING_CONFIG.FREE_CREDITS 
        },
        usage: { ideas_generated: 0, credits_used: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async (newTier) => {
    // Free tier doesn't need Razorpay
    if (newTier === 'free') {
      setChangingPlan(true);
      try {
        await api.updateUserPlan(newTier);
        await loadData();
        setShowPlanModal(false);
      } catch (error) {
        console.error('Error changing plan:', error);
        alert('Failed to change plan. Please try again.');
      } finally {
        setChangingPlan(false);
      }
      return;
    }
    
    // Paid plans go through Razorpay
    setChangingPlan(true);
    try {
      // Load Razorpay script if not loaded
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      // Create order on backend
      const orderData = await api.createPaymentOrder(newTier);
      
      // Configure Razorpay options
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'FirstCut',
        description: `${orderData.plan_name} Plan Subscription`,
        order_id: orderData.order_id,
        handler: async (response) => {
          // Payment successful, verify on backend
          try {
            const verifyResult = await api.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            setPaymentMessage({ 
              type: 'success', 
              text: verifyResult.message || `Successfully upgraded to ${verifyResult.plan_name} plan!` 
            });
            await loadData();
            setShowPlanModal(false);
          } catch (error) {
            console.error('Payment verification failed:', error);
            setPaymentMessage({ 
              type: 'error', 
              text: 'Payment received but verification failed. Please contact support.' 
            });
          }
          setChangingPlan(false);
        },
        prefill: {
          email: orderData.user_email,
          name: orderData.user_name
        },
        theme: {
          color: '#6366f1' // Indigo to match app theme
        },
        modal: {
          ondismiss: () => {
            setChangingPlan(false);
            setPaymentMessage({ type: 'info', text: 'Payment was cancelled. No changes made to your plan.' });
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Error creating payment order:', error);
      alert(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setChangingPlan(false);
    }
  };

  // Handle buying credit packs
  const handleBuyPack = async (packId) => {
    setBuyingPack(packId);
    try {
      // Load Razorpay script if not loaded
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      // Create order for credit pack on backend
      const orderData = await api.createPackOrder(packId);
      
      // Configure Razorpay options
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'FirstCut',
        description: `${orderData.pack_name} - ${orderData.credits} Credits`,
        order_id: orderData.order_id,
        handler: async (response) => {
          // Payment successful, verify on backend
          try {
            const verifyResult = await api.verifyPackPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            setPaymentMessage({ 
              type: 'success', 
              text: verifyResult.message || `Successfully added ${verifyResult.credits_added} credits!` 
            });
            await loadData();
            setShowPlanModal(false);
          } catch (error) {
            console.error('Credit pack verification failed:', error);
            setPaymentMessage({ 
              type: 'error', 
              text: 'Payment received but verification failed. Please contact support.' 
            });
          }
          setBuyingPack(null);
        },
        prefill: {
          email: orderData.user_email,
          name: orderData.user_name
        },
        theme: {
          color: '#6366f1' // Indigo to match app theme
        },
        modal: {
          ondismiss: () => {
            setBuyingPack(null);
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Error creating credit pack order:', error);
      alert(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setBuyingPack(null);
    }
  };

  // Handle subscribing to a monthly plan
  const handleSubscribe = async (planId) => {
    setSubscribingPlan(planId);
    try {
      // Load Razorpay script if not loaded
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      // Create subscription on backend
      const subData = await api.createSubscription(planId);
      
      // Configure Razorpay subscription options
      const options = {
        key: subData.key_id,
        subscription_id: subData.subscription_id,
        name: 'FirstCut',
        description: `${subData.plan_name} - ${subData.credits_per_month} credits/month`,
        handler: async (response) => {
          // Subscription authenticated, verify on backend
          try {
            const verifyResult = await api.verifySubscription({
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            setPaymentMessage({ 
              type: 'success', 
              text: verifyResult.message || `Subscription activated! ${verifyResult.credits_per_month} credits/month.` 
            });
            await loadData();
            setShowPlanModal(false);
          } catch (error) {
            console.error('Subscription verification failed:', error);
            setPaymentMessage({ 
              type: 'error', 
              text: 'Subscription created but verification failed. Please contact support.' 
            });
          }
          setSubscribingPlan(null);
        },
        prefill: {
          email: subData.user_email,
          name: subData.user_name
        },
        theme: {
          color: '#6366f1'
        },
        modal: {
          ondismiss: () => {
            setSubscribingPlan(null);
          }
        }
      };

      // Open Razorpay checkout for subscription
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert(error.response?.data?.detail || 'Failed to start subscription. Please try again.');
      setSubscribingPlan(null);
    }
  };

  // Handle cancelling subscription
  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will keep your credits until the end of the billing period.')) {
      return;
    }
    
    setCancellingSubscription(true);
    try {
      await api.cancelSubscription();
      setPaymentMessage({ 
        type: 'info', 
        text: 'Subscription cancelled. Your credits remain active until the end of the billing period.' 
      });
      await loadData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert(error.response?.data?.detail || 'Failed to cancel subscription. Please contact support.');
    } finally {
      setCancellingSubscription(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground text-sm">Loading usage data...</p>
      </div>
    </div>
  );
  
  if (!usage) return (
    <div className="p-8 text-center">
      <FiAlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
      <p className="text-foreground font-medium mb-2">Unable to load usage data</p>
      <p className="text-muted-foreground text-sm mb-4">Please try refreshing the page</p>
      <button 
        onClick={() => window.location.reload()} 
        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
      >
        Refresh
      </button>
    </div>
  );

  // Use credits-based calculations with hybrid system
  const credits = usage.credits || {
    used: usage.usage?.credits_used || 0,
    limit: usage.limits?.credits_per_month || 40,
    subscription_remaining: 0,
    bonus_credits: 0,
    total_available: 0,
    ideas_equivalent: 0
  };
  // Ensure all values are set
  credits.subscription_remaining = credits.subscription_remaining ?? Math.max(0, credits.limit - credits.used);
  credits.bonus_credits = credits.bonus_credits ?? 0;
  credits.total_available = credits.total_available ?? (credits.subscription_remaining + credits.bonus_credits);
  credits.ideas_equivalent = credits.ideas_equivalent || Math.floor(credits.total_available / 10);
  
  const creditsPercent = Math.min((credits.used / credits.limit) * 100, 100);
  
  // All plans are D2C now (no more agency distinction)
  const currentPlan = plans.find(p => p.id === usage.tier);
  const isFreeTier = usage.tier === 'free';

  // Get feature list for plan cards
  const getFeaturesList = (plan) => {
    const features = [];
    features.push(`${plan.credits_per_month >= 99999 ? 'Unlimited' : plan.credits_per_month} credits/mo`);
    features.push(`≈ ${plan.ideas_equivalent || Math.floor(plan.credits_per_month / 10)} full ideas`);
    features.push(`${plan.clients === 'Unlimited' ? 'Unlimited' : plan.clients} client${plan.clients === 1 ? '' : 's'}`);
    if (plan.team_seats > 1) features.push(`${plan.team_seats} team seats`);
    if (plan.features?.ai_learning) features.push('AI Learning from approvals');
    if (plan.features?.client_intelligence) features.push('Client Intelligence');
    if (plan.features?.refinements) features.push('Content refinements');
    if (plan.features?.priority_support) features.push('Priority support');
    if (plan.features?.client_reports) features.push('Client reports');
    if (plan.features?.api_access) features.push('API access');
    if (plan.features?.white_label) features.push('White-label');
    return features;
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Usage Dashboard</h1>
        <p className="text-muted-foreground mt-1">Track your content generation and credits usage</p>
      </div>
      
      {/* Payment Success/Info Message */}
      {paymentMessage && (
        <div className={`rounded-xl p-4 flex items-center gap-3 ${
          paymentMessage.type === 'success' 
            ? 'bg-emerald-500/10 border border-emerald-500/20' 
            : 'bg-primary/10 border border-primary/20'
        }`}>
          {paymentMessage.type === 'success' ? (
            <FiCheckCircle className="text-emerald-400" size={24} />
          ) : (
            <FiAlertCircle className="text-primary" size={24} />
          )}
          <span className={paymentMessage.type === 'success' ? 'text-emerald-400' : 'text-primary'}>
            {paymentMessage.text}
          </span>
          <button 
            onClick={() => setPaymentMessage(null)}
            className="ml-auto text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>
      )}
      
      {/* Plan Banner */}
      <div className={`rounded-xl p-6 ${
        isFreeTier 
          ? 'bg-gradient-to-r from-secondary to-muted' 
          : 'bg-gradient-to-r from-primary to-accent'
      }`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-heading font-bold text-foreground">{usage.tier.toUpperCase()} Plan</h2>
              {isFreeTier && (
                <span className="px-2 py-0.5 bg-amber-400 text-amber-900 text-xs font-bold rounded">
                  TRIAL
                </span>
              )}
            </div>
            <p className="text-muted-foreground">
              {currentPlan?.price_display || (isFreeTier ? '40 free trial credits' : `₹${usage.limits.price?.toLocaleString()}/month`)}
            </p>
            <div className="text-sm text-muted-foreground/70 mt-1 space-y-0.5">
              {isFreeTier ? (
                <>
                  <p>
                    <span className="text-primary font-semibold">{Math.round(credits.subscription_remaining)}</span> of {credits.limit} free credits remaining
                  </p>
                  <p className="text-amber-400 text-xs">
                    One-time trial credits • Upgrade for monthly credits
                  </p>
                </>
              ) : (
                <>
                  <p>
                    <span className="text-primary font-semibold">{Math.round(credits.subscription_remaining)}</span> of {credits.limit} monthly credits
                    {credits.addon_credits > 0 && (
                      <span className="ml-2">
                        + <span className="text-emerald-400 font-semibold">{Math.round(credits.addon_credits)}</span> add-on credits
                      </span>
                    )}
                  </p>
                  <p>
                    <span className="text-foreground font-semibold">{Math.round(credits.total_available)}</span> total available
                    <span className="text-muted-foreground/50 mx-2">•</span>
                    ≈ {credits.ideas_equivalent} full ideas
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {!isFreeTier && (
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Billing Period</div>
                <div className="font-medium text-foreground">{usage.usage.month}</div>
              </div>
            )}
            <button
              onClick={() => setShowPlanModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-card/80 hover:bg-card text-foreground rounded-lg transition-colors border border-border/50"
              data-testid="change-plan-btn"
            >
              <FiArrowUp size={16} />
              {isFreeTier ? 'Upgrade' : 'Change Plan'}
            </button>
          </div>
        </div>
      </div>

      {/* Upgrade CTA for Free Users */}
      {isFreeTier && (
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-500/20 rounded-lg">
              <FiStar className="text-amber-400" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-foreground mb-1">Unlock Full Potential</h3>
              <p className="text-muted-foreground text-sm mb-3">
                Subscribe for just ₹5,499/mo and get 100 credits (~8 ideas) every month. Cancel anytime.
              </p>
              <button
                onClick={() => setShowPlanModal(true)}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
              >
                View Plans
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Selection Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-border/50">
            <div className="p-8">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-heading font-bold text-foreground">Get More Credits</h2>
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary"
                >
                  <FiX size={20} />
                </button>
              </div>
              <p className="text-muted-foreground mb-6">Choose how you&apos;d like to get credits</p>
              
              {/* Current Subscription Banner */}
              {userSubscription && (
                <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FiRepeat className="text-emerald-400" size={24} />
                      <div>
                        <p className="font-semibold text-foreground">Active Subscription: {userSubscription.plan_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {userSubscription.credits_per_month} credits/month • Next billing: {new Date(userSubscription.next_billing).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancellingSubscription}
                      className="text-sm text-red-400 hover:text-red-300 underline"
                    >
                      {cancellingSubscription ? 'Cancelling...' : 'Cancel Subscription'}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Purchase Mode Switcher */}
              <div className="flex gap-2 mb-6 p-1 bg-secondary rounded-lg w-fit">
                <button
                  onClick={() => setPurchaseMode('subscription')}
                  className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 ${
                    purchaseMode === 'subscription' 
                      ? 'bg-card text-primary shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FiRepeat size={16} />
                  Monthly Subscription
                  <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">Recommended</span>
                </button>
                <button
                  onClick={() => setPurchaseMode('one-time')}
                  className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 ${
                    purchaseMode === 'one-time' 
                      ? 'bg-card text-primary shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FiZap size={16} />
                  Top-up Add-ons
                </button>
              </div>

              {/* Subscription Plans */}
              {purchaseMode === 'subscription' && (
                <div className="mb-6">
                  <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <p className="text-sm text-emerald-400">
                      <FiCheck className="inline mr-2" />
                      <strong>Monthly Subscription:</strong> Fresh credits every month. Auto-renews, cancel anytime.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {subscriptionPlans.map((plan) => {
                      const isCurrentSub = userSubscription?.plan_id === plan.id;
                      
                      return (
                        <div
                          key={plan.id}
                          className={`relative rounded-xl p-6 border-2 transition-all ${
                            isCurrentSub 
                              ? 'border-emerald-500 bg-emerald-500/10' 
                              : plan.is_popular
                              ? 'border-accent bg-accent/5'
                              : 'border-border/50 hover:border-primary/30 bg-card'
                          }`}
                        >
                          {isCurrentSub && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full whitespace-nowrap">
                              Current Subscription
                            </div>
                          )}
                          {plan.is_popular && !isCurrentSub && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full whitespace-nowrap">
                              Most Popular
                            </div>
                          )}
                          
                          <h3 className="text-xl font-bold text-foreground mt-2">{plan.name}</h3>
                          <div className="text-3xl font-heading font-bold text-foreground mb-1 mt-2">
                            {plan.price_display}
                          </div>
                          
                          <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-foreground font-bold">{plan.credits} credits/month</span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              ≈ {plan.approx_posts} posts/month
                            </div>
                          </div>
                          
                          <ul className="text-sm text-muted-foreground space-y-2 mb-6">
                            <li className="flex items-start gap-2">
                              <FiCheck className="text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span>Fresh credits every month</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <FiCheck className="text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span>Auto-renews monthly</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <FiCheck className="text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span>Cancel anytime</span>
                            </li>
                          </ul>
                          
                          <button
                            onClick={() => !isCurrentSub && !userSubscription && handleSubscribe(plan.id)}
                            disabled={isCurrentSub || subscribingPlan === plan.id || (userSubscription && !isCurrentSub)}
                            className={`w-full py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                              isCurrentSub
                                ? 'bg-emerald-500/20 text-emerald-400 cursor-not-allowed'
                              : userSubscription
                                ? 'bg-secondary text-muted-foreground cursor-not-allowed'
                              : plan.is_popular
                                ? 'bg-accent text-accent-foreground hover:bg-accent/90'
                                : 'bg-primary text-primary-foreground hover:bg-primary/90'
                            }`}
                            data-testid={`subscribe-${plan.id}`}
                          >
                            {subscribingPlan === plan.id ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Processing...
                              </>
                            ) : isCurrentSub ? (
                              'Current Plan'
                            ) : userSubscription ? (
                              'Cancel current first'
                            ) : (
                              <>
                                <FiRepeat size={16} />
                                Subscribe
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Add-On Credit Packs */}
              {purchaseMode === 'one-time' && (
                <div className="mb-6">
                  <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <p className="text-sm text-amber-400">
                      <FiAlertCircle className="inline mr-2" />
                      <strong>Add-on packs expire in 90 days.</strong> Best for small top-ups when you run out mid-month. For regular use, subscriptions offer better value.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto">
                    {creditPacks.map((pack) => (
                      <div
                        key={pack.id}
                        className="relative rounded-lg p-4 border text-center transition-all hover:border-primary/50 border-border/50 bg-card"
                      >
                        <div className="font-semibold text-foreground mt-1">{pack.name}</div>
                        <div className="text-xs text-muted-foreground mb-1">{pack.credits} credits ≈ {pack.ideas_equivalent} posts</div>
                        <div className="text-xl font-bold text-foreground">{pack.price_display}</div>
                        <div className="text-xs text-amber-400">Expires 90 days from purchase</div>
                        <button
                          className={`mt-3 w-full py-1.5 text-sm rounded transition-colors flex items-center justify-center gap-2 ${
                            buyingPack === pack.id 
                              ? 'bg-primary/50 text-primary-foreground cursor-wait'
                              : 'bg-primary text-primary-foreground hover:bg-primary/90'
                          }`}
                          onClick={() => handleBuyPack(pack.id)}
                          disabled={buyingPack === pack.id}
                          data-testid={`buy-pack-${pack.id}`}
                        >
                          {buyingPack === pack.id ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Processing...
                            </>
                          ) : (
                            'Buy Now'
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end pt-4 border-t border-border/50">
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="px-6 py-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Credits Used - Primary metric */}
        <div className="bg-card rounded-xl shadow-sm p-6 border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center">
              <FiZap className="mr-2 text-primary" />
              Credits {isFreeTier ? 'Remaining' : 'Used'}
            </h3>
          </div>
          <div className="text-3xl font-bold text-foreground mb-1">
            {isFreeTier ? Math.round(credits.subscription_remaining) : Math.round(credits.used)}
          </div>
          <div className="text-sm text-muted-foreground">
            {isFreeTier 
              ? `of ${credits.limit} free trial credits`
              : `of ${credits.limit} this month`
            }
          </div>
          <div className="w-full bg-secondary rounded-full h-2 mt-4">
            <div 
              className={`h-2 rounded-full transition-all ${creditsPercent > 80 ? 'bg-orange-500' : 'bg-primary'}`} 
              style={{width: `${isFreeTier ? 100 - creditsPercent : creditsPercent}%`}}
            ></div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            ≈ {credits.ideas_equivalent} full ideas remaining
          </p>
          {isFreeTier && credits.subscription_remaining < credits.limit && (
            <p className="text-xs text-amber-400 mt-1">
              Trial credits don't reset • Upgrade for monthly credits
            </p>
          )}
          {!isFreeTier && creditsPercent > 80 && (
            <p className="text-xs text-orange-400 mt-1">⚠️ Approaching limit</p>
          )}
          {/* Add-on credits display */}
          {credits.addon_credits > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <p className="text-xs text-emerald-400">
                +{credits.addon_credits} add-on credits
                {credits.addon_expires_at && (
                  <span className="text-muted-foreground ml-1">
                    (expires {new Date(credits.addon_expires_at).toLocaleDateString()})
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Ideas Generated */}
        <div className="bg-card rounded-xl shadow-sm p-6 border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center">
              <FiFileText className="mr-2 text-emerald-400" />
              Ideas Generated
            </h3>
          </div>
          <div className="text-3xl font-bold text-foreground mb-1">{usage.usage?.ideas_generated || 0}</div>
          <div className="text-sm text-muted-foreground">full ideas this month</div>
        </div>

        {/* Images Generated */}
        <div className="bg-card rounded-xl shadow-sm p-6 border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center">
              <FiImage className="mr-2 text-purple-400" />
              Images Generated
            </h3>
          </div>
          <div className="text-3xl font-bold text-foreground mb-1">{usage.usage?.images_generated || 0}</div>
          <div className="text-sm text-muted-foreground">images this month</div>
        </div>

        {/* Refinements */}
        <div className="bg-card rounded-xl shadow-sm p-6 border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center">
              <FiTrendingUp className="mr-2 text-amber-400" />
              Refinements
            </h3>
          </div>
          <div className="text-3xl font-bold text-foreground mb-1">{usage.usage?.refinements_count || 0}</div>
          <div className="text-sm text-muted-foreground">refinements this month</div>
        </div>
      </div>

      {/* Moderation Stats */}
      {moderation && moderation.total_blocked > 0 && (
        <div className="bg-card rounded-xl shadow-sm p-6 border border-border">
          <h3 className="font-semibold text-foreground mb-4 flex items-center">
            <FiAlertCircle className="mr-2 text-red-400" />
            Content Moderation
          </h3>
          <p className="text-sm text-muted-foreground">
            {moderation.total_blocked} request(s) were blocked this month due to content policy violations.
          </p>
        </div>
      )}
    </div>
  );
}

export default UsageDashboard;
